import React, { useState } from "react";
import { Button, Space, Tooltip, Typography, message } from "antd";
import { BarcodeOutlined, EditOutlined, FilePdfOutlined } from "@ant-design/icons";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link } from "react-router-dom";
import freightApi2 from "../../../service/modules/freight.api";
import {
  FREIGHT_CONTROL_DOCUMENT_ACTIONS,
  findFreightDocumentByActionType,
} from "../../../util/freight/freight-document-utils";
import { canShowFreightControlBoletoAction } from "../../../util/freight/freight-v2-table-utils";
import theme from "../../../theme";
import FreightDocumentsModal from "./FreightDocumentsModal";

const { Text } = Typography;

const actionIconStyle = {
  fontSize: 24,
  color: theme.colors.primary,
};

const linkButtonStyle = {
  padding: "0 4px",
  height: "auto",
  lineHeight: 1,
};

const muiActionIconSx = {
  fontSize: 24,
  color: theme.colors.primary,
};

async function loadFreightDocuments(freightId, cache) {
  if (cache?.[freightId]) return cache[freightId];
  const result = await freightApi2.getById(freightId);
  const payload = result?.data?.data ?? result?.data ?? result;
  return payload || null;
}

function resolveFreightLabel(record) {
  return (
    record?.numberSerial ||
    record?.freight?.numberSerial ||
    record?.freight?.code ||
    record?.freightId ||
    record?.id ||
    ""
  );
}

function FreightControlActions({
  record,
  permissions = {},
  documentCacheRef,
  onEditFinancial,
}) {
  const [loadingKey, setLoadingKey] = useState(null);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [documentsModalLoading, setDocumentsModalLoading] = useState(false);
  const [documentsFreightData, setDocumentsFreightData] = useState(null);
  const freightId = record?.freightId || record?.id;

  const {
    canViewPanel = false,
    canEditFinancial = false,
    canViewDocuments = false,
    canViewBoleto = false,
  } = permissions;

  const showBoletoAction =
    canViewBoleto && canShowFreightControlBoletoAction(record);
  const showDocumentsAction = canViewDocuments;
  const showEditAction = canEditFinancial;
  const showViewFreightAction = !canEditFinancial && canViewPanel;
  const hasVisibleAction =
    showBoletoAction ||
    showDocumentsAction ||
    showEditAction ||
    showViewFreightAction;

  const handleDocumentAction = async (actionType) => {
    if (!freightId || !showBoletoAction) return;

    setLoadingKey(actionType);
    try {
      let freightData = documentCacheRef?.current?.[freightId];
      if (!freightData) {
        freightData = await loadFreightDocuments(freightId);
        if (documentCacheRef?.current && freightData) {
          documentCacheRef.current[freightId] = freightData;
        }
      }

      const document = findFreightDocumentByActionType(
        {
          ...freightData,
          boletoAttachment:
            freightData?.boletoAttachment || record?.boletoAttachment,
        },
        actionType,
      );
      if (document?.url) {
        window.open(document.url, "_blank", "noopener,noreferrer");
        return;
      }

      const actionLabel =
        FREIGHT_CONTROL_DOCUMENT_ACTIONS.find(
          (item) => item.key === actionType,
        )?.label || actionType;

      message.warning(
        `Documento "${actionLabel}" não encontrado para este frete.`,
      );
    } catch (error) {
      console.error("Erro ao buscar documentos do frete:", error);
      message.error("Não foi possível carregar os documentos do frete.");
    } finally {
      setLoadingKey(null);
    }
  };

  const handleOpenDocumentsModal = async () => {
    if (!freightId || !canViewDocuments) return;

    setDocumentsModalOpen(true);
    setDocumentsModalLoading(true);

    try {
      let freightData = documentCacheRef?.current?.[freightId];
      if (!freightData) {
        freightData = await loadFreightDocuments(freightId);
        if (documentCacheRef?.current && freightData) {
          documentCacheRef.current[freightId] = freightData;
        }
      }

      setDocumentsFreightData({
        ...freightData,
        boletoAttachment:
          freightData?.boletoAttachment || record?.boletoAttachment,
        stopping_points: Array.isArray(freightData?.stopping_points)
          ? freightData.stopping_points
          : record?.stopping_points || [],
        attachments: Array.isArray(freightData?.attachments)
          ? freightData.attachments
          : record?.attachments || [],
      });
    } catch (error) {
      console.error("Erro ao carregar documentos do frete:", error);
      message.error("Não foi possível carregar os documentos do frete.");
      setDocumentsModalOpen(false);
    } finally {
      setDocumentsModalLoading(false);
    }
  };

  const handleCloseDocumentsModal = () => {
    setDocumentsModalOpen(false);
    setDocumentsFreightData(null);
  };

  if (!hasVisibleAction) {
    return <Text type="secondary">—</Text>;
  }

  return (
    <>
      <Space>
        {showBoletoAction ? (
          <Tooltip title="Visualizar boleto">
            <Button
              type="link"
              aria-label="Visualizar boleto"
              style={{ ...linkButtonStyle, color: theme.colors.primary }}
              icon={<BarcodeOutlined style={actionIconStyle} />}
              loading={loadingKey === "BOLETO"}
              disabled={
                !freightId || (loadingKey != null && loadingKey !== "BOLETO")
              }
              onClick={() => handleDocumentAction("BOLETO")}
            />
          </Tooltip>
        ) : null}

        {showDocumentsAction ? (
          <Tooltip title="Ver documentos do frete por ponto de parada">
            <Button
              type="link"
              aria-label="Ver documentos do frete"
              style={{ ...linkButtonStyle, color: theme.colors.primary }}
              icon={<FilePdfOutlined style={actionIconStyle} />}
              loading={documentsModalLoading}
              disabled={!freightId || loadingKey != null}
              onClick={handleOpenDocumentsModal}
            />
          </Tooltip>
        ) : null}

        {showEditAction ? (
          <Tooltip title="Editar registro financeiro">
            <Button
              type="link"
              aria-label="Editar registro financeiro"
              style={{ ...linkButtonStyle, color: theme.colors.primary }}
              icon={<EditOutlined style={actionIconStyle} />}
              onClick={() => onEditFinancial?.(record)}
              disabled={!freightId}
            />
          </Tooltip>
        ) : showViewFreightAction ? (
          <Tooltip title="Visualizar frete">
            <Link
              to={`/freightCopy/${freightId}`}
              state={{ viewOnly: true }}
              aria-label="Visualizar frete"
            >
              <Button
                type="link"
                style={{ ...linkButtonStyle, color: theme.colors.primary }}
                icon={<VisibilityIcon sx={muiActionIconSx} />}
                disabled={!freightId}
              />
            </Link>
          </Tooltip>
        ) : null}
      </Space>

      {showDocumentsAction ? (
        <FreightDocumentsModal
          open={documentsModalOpen}
          onClose={handleCloseDocumentsModal}
          loading={documentsModalLoading}
          freightData={documentsFreightData}
          freightLabel={resolveFreightLabel(record)}
        />
      ) : null}
    </>
  );
}

export default FreightControlActions;
